<script setup>
import { formatCurrency } from '@core/utils/formatters'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import {
  pricingPlanCadenceLabel,
  pricingPlanLifecycleColor,
  pricingPlanLifecycleLabel,
  pricingPlanTypeLabel,
  usePricingPlansStore,
} from '@/stores/pricingPlans'

definePage({
  meta: {
    navActiveLink: 'pricing-plans',
  },
})

const route = useRoute()
const router = useRouter()
const store = usePricingPlansStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const retireDialog = ref(false)
const deleteDialog = ref(false)

const form = ref({
  name: '',
  description: '',
  plan_type: 'hourly',
  price_euros: null,
  billing_cadence: 'monthly',
  session_count: 10,
  validity_days: 90,
})

const plan = computed(() => store.current)
const isDraft = computed(() => plan.value?.lifecycle_state === 'draft')
const isPublished = computed(() => plan.value?.lifecycle_state === 'published')
const isRetired = computed(() => plan.value?.lifecycle_state === 'retired')
const commercialLocked = computed(() => isPublished.value || isRetired.value)
const canEditMetadata = computed(() => isDraft.value || isPublished.value)

const syncForm = () => {
  const p = store.current
  if (!p)
    return
  form.value = {
    name: p.name || '',
    description: p.description || '',
    plan_type: p.plan_type || 'hourly',
    price_euros: p.price_cents != null ? p.price_cents / 100 : null,
    billing_cadence: p.billing_cadence || 'monthly',
    session_count: p.session_count || 10,
    validity_days: p.validity_days || 90,
  }
}

const load = async () => {
  formError.value = ''
  await store.fetchPricingPlan(route.params.id)
  syncForm()
  await store.fetchClientsOnPlan(route.params.id).catch(() => null)
}

onMounted(() => {
  load().catch(() => null)
})

const eurosToCents = euros => Math.round(Number(euros) * 100)

const save = async () => {
  formError.value = ''
  formSuccess.value = ''

  let payload
  if (isDraft.value) {
    payload = {
      name: form.value.name,
      description: form.value.description || null,
      plan_type: form.value.plan_type,
      price_cents: eurosToCents(form.value.price_euros),
      currency: 'EUR',
      billing_cadence: form.value.plan_type === 'subscription' ? form.value.billing_cadence : null,
      session_count: form.value.plan_type === 'session_package' ? Number(form.value.session_count) : null,
      validity_days: form.value.plan_type === 'session_package' ? Number(form.value.validity_days) : null,
    }
  }
  else {
    payload = {
      name: form.value.name,
      description: form.value.description || null,
    }
  }

  try {
    await store.updatePricingPlan(route.params.id, payload)
    syncForm()
    formSuccess.value = t('pricingPlans.saved')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const publish = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.publishPricingPlan(route.params.id)
    syncForm()
    formSuccess.value = t('pricingPlans.published')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmRetire = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.retirePricingPlan(route.params.id)
    retireDialog.value = false
    syncForm()
    formSuccess.value = t('pricingPlans.retired')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmDelete = async () => {
  formError.value = ''
  try {
    await store.destroyPricingPlan(route.params.id)
    deleteDialog.value = false
    await router.push({ name: 'pricing-plans' })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}
</script>

<template>
  <div>
    <div class="mb-4">
      <RouterLink
        class="text-medium-emphasis text-decoration-none"
        :to="{ name: 'pricing-plans' }"
      >
        ← {{ $t('pricingPlans.backToCatalog') }}
      </RouterLink>
    </div>

    <div
      v-if="store.loading && !plan"
      class="text-medium-emphasis"
      role="status"
    >
      {{ $t('pricingPlans.loading') }}
    </div>

    <template v-else-if="plan">
      <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
        <div>
          <h1 class="text-h4 mb-1">
            {{ plan.name }}
          </h1>
          <div class="d-flex flex-wrap align-center gap-2">
            <VChip
              size="small"
              label
              :color="pricingPlanLifecycleColor(plan.lifecycle_state)"
            >
              <span class="visually-hidden">{{ $t('pricingPlans.statusPrefix') }}</span>
              {{ pricingPlanLifecycleLabel(plan.lifecycle_state) }}
            </VChip>
            <span class="text-medium-emphasis">
              {{ pricingPlanTypeLabel(plan.plan_type) }}
              ·
              {{ formatCurrency(plan.price_cents, plan.currency || 'EUR') }}
            </span>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <VBtn
            v-if="isDraft"
            color="primary"
            :loading="store.saving"
            @click="publish"
          >
            {{ $t('pricingPlans.publish') }}
          </VBtn>
          <VBtn
            v-if="isPublished"
            color="warning"
            variant="tonal"
            :disabled="store.saving"
            @click="retireDialog = true"
          >
            {{ $t('pricingPlans.retire') }}
          </VBtn>
          <VBtn
            v-if="isDraft"
            color="error"
            variant="tonal"
            :disabled="store.saving"
            @click="deleteDialog = true"
          >
            {{ $t('common.delete') }}
          </VBtn>
        </div>
      </div>

      <VAlert
        v-if="formSuccess"
        type="success"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ formSuccess }}
      </VAlert>

      <VAlert
        v-if="formError || store.error"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
      >
        {{ formError || store.error }}
      </VAlert>

      <VAlert
        v-if="isPublished"
        type="info"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('pricingPlans.publishedLockHint') }}
      </VAlert>

      <VAlert
        v-if="isRetired"
        type="info"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('pricingPlans.retiredHint') }}
      </VAlert>

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardItem>
          <VCardTitle>{{ $t('pricingPlans.detailTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText class="d-flex flex-column gap-4">
          <VTextField
            v-model="form.name"
            :label="$t('pricingPlans.fields.name')"
            :disabled="!canEditMetadata || store.saving"
          />
          <VTextarea
            v-model="form.description"
            :label="$t('pricingPlans.fields.description')"
            rows="2"
            auto-grow
            :disabled="!canEditMetadata || store.saving"
          />
          <VSelect
            v-model="form.plan_type"
            :label="$t('pricingPlans.fields.planType')"
            :disabled="commercialLocked || store.saving"
            :items="[
              { title: $t('pricingPlans.planTypes.subscription'), value: 'subscription' },
              { title: $t('pricingPlans.planTypes.session_package'), value: 'session_package' },
              { title: $t('pricingPlans.planTypes.hourly'), value: 'hourly' },
            ]"
          />
          <VTextField
            v-model.number="form.price_euros"
            :label="$t('pricingPlans.fields.price')"
            type="number"
            min="0.01"
            step="0.01"
            prefix="€"
            :disabled="commercialLocked || store.saving"
          />
          <VSelect
            v-if="form.plan_type === 'subscription'"
            v-model="form.billing_cadence"
            :label="$t('pricingPlans.fields.billingCadence')"
            :disabled="commercialLocked || store.saving"
            :items="[
              { title: $t('pricingPlans.cadences.monthly'), value: 'monthly' },
              { title: $t('pricingPlans.cadences.quarterly'), value: 'quarterly' },
              { title: $t('pricingPlans.cadences.semiannual'), value: 'semiannual' },
              { title: $t('pricingPlans.cadences.annual'), value: 'annual' },
            ]"
          />
          <VTextField
            v-if="form.plan_type === 'session_package'"
            v-model.number="form.session_count"
            :label="$t('pricingPlans.fields.sessionCount')"
            type="number"
            min="1"
            step="1"
            :disabled="commercialLocked || store.saving"
          />
          <VTextField
            v-if="form.plan_type === 'session_package'"
            v-model.number="form.validity_days"
            :label="$t('pricingPlans.fields.validityDays')"
            type="number"
            min="1"
            step="1"
            :disabled="commercialLocked || store.saving"
          />

          <div
            v-if="commercialLocked && plan.billing_cadence"
            class="text-body-2 text-medium-emphasis"
          >
            {{ $t('pricingPlans.fields.billingCadence') }}:
            {{ pricingPlanCadenceLabel(plan.billing_cadence) }}
          </div>

          <VBtn
            v-if="canEditMetadata"
            color="primary"
            class="align-self-start"
            :loading="store.saving"
            @click="save"
          >
            {{ $t('common.save') }}
          </VBtn>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
      >
        <VCardItem>
          <VCardTitle>{{ $t('pricingPlans.clientsOnPlanTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="!store.clientsOnPlan.length"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('pricingPlans.clientsOnPlanEmpty') }}
          </VAlert>
          <VList
            v-else
            lines="two"
            border
            rounded
          >
            <VListItem
              v-for="client in store.clientsOnPlan"
              :key="client.subscription_id"
              :title="client.name || client.email"
              :subtitle="client.email"
              :to="{ name: 'clients-id', params: { id: client.id } }"
            >
              <template #append>
                <StatusChip
                  domain="subscription"
                  :state="client.subscription_lifecycle_state"
                />
              </template>
            </VListItem>
          </VList>
        </VCardText>
      </VCard>
    </template>

    <ConfirmDialog
      v-model="retireDialog"
      :title="$t('pricingPlans.retireConfirm.title')"
      :body="$t('pricingPlans.retireConfirm.body')"
      :confirm-label="$t('pricingPlans.retireConfirm.confirm')"
      :acknowledgment-label="$t('pricingPlans.retireConfirm.ack')"
      require-acknowledgment
      color="warning"
      :loading="store.saving"
      @confirm="confirmRetire"
    />

    <ConfirmDialog
      v-model="deleteDialog"
      :title="$t('pricingPlans.deleteConfirm.title')"
      :body="$t('pricingPlans.deleteConfirm.body')"
      :confirm-label="$t('common.delete')"
      color="error"
      :loading="store.saving"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped>
.visually-hidden {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
